import React from "react";
import { Box, Rating, Stack, Typography } from "@mui/material";
import Image from "next/image";
import { useTranslations } from "next-intl";
import ReadMore from "@/components/common-controls/collette-read-more-toggle";
import { Image as ImageType } from "@/types/dto/common/image";

interface TestimonialCardProps {
  title?: string;
  copy: string;
  reviewerNameSocialMediaHandle?: string;
  starRating?: number;
  ctaUrl?: string;
  ctaTitle?: string;
  testId?: string;
  isReadMore?: boolean;
  bgImage?: ImageType;
}

const TestimonialCard = ({
  title,
  copy,
  reviewerNameSocialMediaHandle,
  starRating,
  ctaUrl,
  ctaTitle,
  testId,
  isReadMore,
  bgImage
}: TestimonialCardProps) => {
  const t = useTranslations("HomePage");

  // Compute styles once
  const hasBgImage = Boolean(bgImage?.imageUrl);
  const textColor = hasBgImage ? "common.white" : "text.primary";
  const accentColor = hasBgImage ? "var(--theme-palette-common-white)" : "var(--theme-palette-text-primary)";

  const contentZIndex = {
    position: "relative" as const,
    zIndex: 1
  };

  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: "background.default",
        borderRadius: 3,
        minHeight: 180,
        height: isReadMore ? "100%" : "auto",
        p: "24px",
        display: "flex",
        flexDirection: "column"
      }}
      data-testid={testId}
    >
      {hasBgImage && bgImage?.imageUrl && (
        <Image
          src={bgImage.imageUrl}
          alt={bgImage.altText || "testimonial card background"}
          fill
          style={{
            position: "absolute",
            objectFit: "cover",
            borderRadius: "inherit",
            filter: "brightness(0.5)",
            zIndex: 0
          }}
          data-testid={`${testId}-background-image`}
        />
      )}
      <Stack
        display="flex"
        direction="row"
        justifyContent="space-between"
        marginBottom="14px"
        sx={contentZIndex}
      >
        <Image
          src="https://i.gocollette.com/homepage/quotes_blue.svg"
          alt={title || "Quote"}
          width={46}
          height={32}
          data-testid={`${testId}-quote-icon`}
        />
        {starRating !== undefined && (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography fontSize={12} color={textColor} fontWeight={500} marginBottom="4px" data-testid={`${testId}-tour-rating-label`}>
              {t("Tour Rating")}
            </Typography>
            <Rating
              value={starRating}
              disabled
              precision={0.5}
              readOnly
              size="small"
              data-testid={`${testId}-tour-rating-${starRating}`}
              sx={{
                ...contentZIndex,
                "&.Mui-disabled": {
                  color: "accent.gold",
                  opacity: 1
                }
              }}
            />
          </Box>
        )}
      </Stack>
      {isReadMore ? (
        <ReadMore
          description={copy}
          linesShown={5}
          testId={testId || "testimonial-card"}
          textSx={{
            color: textColor,
            fontWeight: 500,
            lineHeight: "150%",
            ...contentZIndex
          }}
          textComponent="p"
        />
      ) : (
        <Typography
          color={textColor}
          component="p"
          variant="subtitle_lg"
          fontWeight={500}
          lineHeight="150%"
          sx={contentZIndex}
        >
          {copy}
        </Typography>
      )}
      {reviewerNameSocialMediaHandle && (
        <Typography
          variant="subtitle_lg"
          color={accentColor}
          fontWeight={700}
          marginTop="14px"
          sx={contentZIndex}
          data-testid={`${testId}-reviewer-name`}
        >
          {reviewerNameSocialMediaHandle}
        </Typography>
      )}
      {ctaUrl && ctaTitle && (
        <Typography
          variant="subtitle_lg"
          component="p"
          fontWeight={500}
          sx={{
            textDecoration: "underline",
            textDecorationColor: accentColor,
            ...contentZIndex
          }}
        >
          <a href={ctaUrl} style={{ color: accentColor }} data-testid={`${testId}-cta-link`}>
            {ctaTitle}
          </a>
        </Typography>
      )}
    </Box>
  );
};

export default TestimonialCard;
